/**
 * ConsequenceCollector - Captures consequence events for statistical analysis
 *
 * Layer 1: Data Collection
 *
 * Purpose:
 * - Extract statistical dimensions from consequence events
 * - Track consequence type distribution
 * - Monitor auto-generation vs manual creation
 * - Track escalation patterns
 * - Measure time-to-action metrics
 *
 * Dimensions Captured:
 * - Student (who received the consequence)
 * - Consequence type (detention, suspension, etc.)
 * - Generation method (auto vs manual)
 * - Escalation tracking (was it escalated)
 * - Action status (pending, actioned)
 * - Time dimension
 *
 * Integration:
 * - Called by ConsequenceService after consequence creation
 * - Non-blocking - failures don't affect consequence creation
 */

import { BaseCollector } from './BaseCollector.js';

export class ConsequenceCollector extends BaseCollector {
  /**
   * Collect a consequence event and extract all statistical dimensions
   * @param {Object} consequence - The created consequence object
   * @returns {Promise<void>}
   */
  async collect(consequence) {
    await this.safeCollect(async () => {
      // Validate required fields
      const requiredFields = ['studentId', 'studentName', 'type', 'issuedAt'];
      if (!this.validateEvent(consequence, requiredFields)) {
        return;
      }

      const timestamp = this.normalizeTimestamp(consequence.issuedAt);

      // Build comprehensive event with all dimensions
      const event = {
        // Event metadata
        type: 'consequence',
        timestamp: timestamp,
        dateKey: this.getDateKey(timestamp),
        consequenceId: consequence.id,

        // Student dimension
        studentId: consequence.studentId,
        studentName: consequence.studentName,

        // Consequence type dimension
        consequenceType: consequence.type,
        consequenceTypeId: consequence.typeId || null,
        consequenceTypeName: consequence.typeName || consequence.type,

        // Generation tracking
        autoGenerated: consequence.autoGenerated || false,
        triggeredByRule: consequence.triggeredByRule || null,
        triggeredByRuleName: consequence.ruleName || null,
        ruleType: this.inferRuleType(consequence),

        // Escalation tracking
        wasEscalated: !!consequence.escalatedFrom,
        escalatedFrom: consequence.escalatedFrom || null,
        escalatedBy: consequence.escalatedBy || null,
        escalationRuleName: consequence.escalationRuleName || null,

        // Action tracking
        status: consequence.status || 'pending',
        requiresAction: consequence.requiresAction || false,
        actioned: consequence.actioned || false,
        actionedAt: consequence.actionedAt || null,

        // Class dimension
        classId: consequence.classId || 'unassigned',
        className: consequence.className || 'Unassigned',

        // Time dimension
        hour: timestamp.getHours(),
        dayOfWeek: timestamp.getDay(),
        period: this.getPeriod(timestamp),

        // Triggering mention tracking
        triggeredByMentions: this.extractMentionIds(consequence),
        mentionCount: this.extractMentionIds(consequence).length,

        // Severity (if available)
        severity: consequence.severity || null
      };

      this.log('Collecting consequence event', {
        studentName: event.studentName,
        type: event.consequenceType,
        autoGenerated: event.autoGenerated,
        wasEscalated: event.wasEscalated
      });

      // Send to storage layer
      await this.storage.recordConsequence(event);
    });
  }

  /**
   * Extract mention IDs from various consequence fields
   * @param {Object} consequence - Consequence object
   * @returns {Array<string>}
   */
  extractMentionIds(consequence) {
    // Try multiple field names
    const ids = consequence.triggeredBy
      || consequence.triggeringMentions
      || consequence.mentionIds
      || [];

    return Array.isArray(ids) ? ids : [];
  }

  /**
   * Infer rule type from consequence metadata
   * @param {Object} consequence - Consequence object
   * @returns {string|null}
   */
  inferRuleType(consequence) {
    if (consequence.triggeredByThreshold) {
      return 'threshold';
    }
    if (consequence.triggeredByRule) {
      // Could enhance this by looking up rule type from ruleId
      // For now, use naming convention if available
      if (consequence.ruleName?.toLowerCase().includes('escalation')) {
        return 'escalation';
      }
      if (consequence.ruleName?.toLowerCase().includes('zero')) {
        return 'zero-tolerance';
      }
      if (consequence.ruleName?.toLowerCase().includes('mention')) {
        return 'mention';
      }
      return 'rule';
    }
    return null;
  }

  /**
   * Determine school period from timestamp
   * @param {Date} timestamp - Event timestamp
   * @returns {string}
   */
  getPeriod(timestamp) {
    const hour = timestamp.getHours();
    const minute = timestamp.getMinutes();
    const totalMinutes = hour * 60 + minute;

    if (totalMinutes < 480) return 'before-school';
    if (totalMinutes < 540) return 'period-1';
    if (totalMinutes < 600) return 'period-2';
    if (totalMinutes < 660) return 'period-3';
    if (totalMinutes < 720) return 'lunch';
    if (totalMinutes < 780) return 'period-4';
    if (totalMinutes < 840) return 'period-5';
    if (totalMinutes < 900) return 'period-6';
    return 'after-school';
  }

  /**
   * Calculate time to action (if consequence is actioned)
   * @param {Object} consequence - Consequence object
   * @returns {number|null} Minutes to action, or null if not actioned
   */
  static calculateTimeToAction(consequence) {
    if (!consequence.actioned || !consequence.actionedAt || !consequence.issuedAt) {
      return null;
    }

    const issued = consequence.issuedAt.toDate ? consequence.issuedAt.toDate() : new Date(consequence.issuedAt);
    const actioned = consequence.actionedAt.toDate ? consequence.actionedAt.toDate() : new Date(consequence.actionedAt);

    const diffMs = actioned - issued;
    return Math.round(diffMs / 1000 / 60); // Convert to minutes
  }

  /**
   * Get consequence type category for grouping
   * @param {string} type - Consequence type
   * @returns {string}
   */
  static getConsequenceTypeCategory(type) {
    const categories = {
      'demerit': 'point-based',
      'detention': 'detention',
      'friday-detention': 'detention',
      'saturday-detention': 'detention',
      'suspension': 'suspension',
      'suspension-pending-review': 'suspension',
      'warning': 'warning',
      'notification': 'notification',
      'contract-consequence': 'contract'
    };

    return categories[type] || 'other';
  }
}
